import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import {
  axiosInstanceForTenantAPI,
  axiosInstanceWithSlugForPublicAPI,
} from '../assets/utils/axiosInstance';
import { Language } from '../interfaces/Language';

type TenantData = {
  id: number;
  name: string;
  policy_text: string;
  logo: number;
  logo_url: string;
  banner: number;
  banner_url: string;
  slug: string;
  is_username_and_password_login_enabled: boolean;
  wb_program_url: string;
  is_mfa_enabled: boolean;
  hotline_number: string;
  mailing_address: string;
  auto_first_response_message: string;
  first_response_deadline_days: number;
  final_response_deadline_days: number;
  is_auto_response_message_enabled: boolean;
  banner_heading: string;
  banner_subheading: string;
  infographic: number;
  infographic_url: string;
  infographic_is_display_on_program_overview_enabled: boolean;
  is_wb_meeting_allowed: boolean;
  base_language: Language;
  language_ids: number[];
  languages: Language[];
};

export type ProgramOverviewData = {
  banner_heading: string;
  banner_subheading: string;
  banner_url: string;
  hotline_number: string;
  mailing_address: string;
  is_wb_meeting_allowed: boolean;
  language_ids: number[];
  languages: Language[];
  logo_url: string;
  name: string;
  policy_text: string;
  overview_infographic_url: string;
};

type TenantState = {
  tenantData: TenantData;
  programOverviewData: ProgramOverviewData;
  languages: Language[];
  isLoading: boolean;
  error: string | null;
};

export const fetchTenantData = createAsyncThunk<
  { type: string; data: TenantData | ProgramOverviewData },
  { type: string },
  { rejectValue: string }
>('', async ({ type }, { rejectWithValue }) => {
  try {
    if (type === 'public') {
      const response = await axiosInstanceWithSlugForPublicAPI().get(
        '/program-overview',
      );

      return { type: type, data: response.data as ProgramOverviewData };
    } else if (type === 'tenant') {
      const response = await axiosInstanceForTenantAPI().get('');
      return { type: type, data: response.data as TenantData };
    } else {
      return rejectWithValue('Invalid type');
    }
  } catch (error) {
    // If an error occurs during the request, reject the thunk with an error message
    return rejectWithValue('Failed to fetch language choices');
  }
});

const initialState: TenantState = {
  tenantData: {
    id: 0,
    name: '',
    policy_text: '',
    logo: 0,
    logo_url: '',
    banner: 0,
    banner_url: '',
    slug: '',
    is_username_and_password_login_enabled: false,
    wb_program_url: '',
    is_mfa_enabled: false,
    hotline_number: '',
    mailing_address: '',
    auto_first_response_message: '',
    first_response_deadline_days: 0,
    final_response_deadline_days: 0,
    is_auto_response_message_enabled: false,
    banner_heading: '',
    banner_subheading: '',
    infographic: 0,
    infographic_url: '',
    infographic_is_display_on_program_overview_enabled: false,
    is_wb_meeting_allowed: false,
    base_language: {
      code: '',
      id: 0,
      name: '',
    },
    language_ids: [],
    languages: [],
  },
  programOverviewData: {
    banner_heading: '',
    banner_subheading: '',
    banner_url: '',
    hotline_number: '',
    mailing_address: '',
    is_wb_meeting_allowed: false,
    language_ids: [],
    languages: [],
    logo_url: '',
    name: '',
    policy_text: '',
    overview_infographic_url: '',
  },
  languages: [],
  isLoading: false,
  error: null,
};

const tenantSlice = createSlice({
  name: 'tenant',
  initialState,
  reducers: {
    setTenantData(state, action) {
      state.tenantData = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchTenantData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTenantData.fulfilled, (state, action) => {
        if (action.payload.type === 'public') {
          state.programOverviewData = action.payload
            .data as ProgramOverviewData;
        } else {
          state.tenantData = action.payload.data as TenantData;
        }
        state.languages = action.payload.data.languages;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(fetchTenantData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Failed to fetch tenant data';
      });
  },
});

export const { setTenantData } = tenantSlice.actions;

export default tenantSlice.reducer;
